<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getBrowserUpdateSettings } from '@/utils/browser-settings'
import { copyText } from '@/utils/clipboard'

withDefaults(defineProps<{ expanded?: boolean }>(), { expanded: false })
const { t } = useI18n()
const browser = getBrowserUpdateSettings()
const pageLink = window.location.href
const copying = ref(false)
const copyState = ref<'idle' | 'copied' | 'failed'>('idle')
const manualText = ref('')

async function copy(value: string): Promise<void> {
  if (copying.value) return
  copying.value = true
  copyState.value = 'idle'
  manualText.value = ''
  let timer: number | undefined
  try {
    const copied = await Promise.race([
      copyText(value),
      new Promise<boolean>(resolve => { timer = window.setTimeout(() => resolve(false), 3_000) }),
    ])
    copyState.value = copied ? 'copied' : 'failed'
  } catch {
    copyState.value = 'failed'
  } finally {
    window.clearTimeout(timer)
    if (copyState.value === 'failed') manualText.value = value
    copying.value = false
  }
}
</script>

<template>
  <details class="browser-help" :open="expanded || undefined">
    <summary>{{ t('captureRecovery.browserHelp') }}</summary>
    <template v-if="browser">
      <p>{{ t('captureRecovery.updateSteps', { browser: browser.name }) }}</p>
      <div class="browser-actions notice-actions">
        <code>{{ browser.url }}</code>
        <el-button :disabled="copying" @click="copy(browser.url)">{{ t('captureRecovery.copyUpdateAddress') }}</el-button>
      </div>
    </template>
    <p v-else>{{ t('captureRecovery.updateMenu') }}</p>
    <div class="browser-actions notice-actions">
      <a href="https://www.microsoft.com/edge/download" target="_blank" rel="noopener noreferrer">{{ t('captureRecovery.downloadEdge') }}</a>
      <a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer">{{ t('captureRecovery.downloadChrome') }}</a>
      <a href="https://www.mozilla.org/firefox/new/" target="_blank" rel="noopener noreferrer">{{ t('captureRecovery.downloadFirefox') }}</a>
      <el-button :disabled="copying" @click="copy(pageLink)">{{ t('captureRecovery.copyPageLink') }}</el-button>
    </div>
    <slot />
    <p v-if="copyState !== 'idle'" role="status">{{ t(copyState === 'copied' ? 'captureRecovery.linkCopied' : 'captureRecovery.linkCopyFailed') }}</p>
    <textarea v-if="manualText" readonly :value="manualText" :aria-label="t('captureRecovery.manualAddress')" rows="2" />
  </details>
</template>

<style scoped>
.browser-help { margin-top: .75rem; color: var(--muted-strong); font-size: var(--font-body); line-height: 1.6; }
summary { width: fit-content; max-width: 100%; padding-block: .25rem; color: var(--text-strong); cursor: pointer; }
p { margin: .5rem 0; }
code { overflow-wrap: anywhere; user-select: all; }
a { color: var(--text-strong); text-decoration: underline; text-underline-offset: .2em; padding-block: .5rem; }
summary:focus-visible, a:focus-visible { outline: .125rem solid var(--primary) !important; outline-offset: .1875rem; }
textarea { box-sizing: border-box; width: 100%; margin-top: .5rem; padding: .5rem; color: var(--text-strong); background: var(--surface); border: .0625rem solid var(--border); border-radius: .25rem; resize: vertical; }
@media (any-pointer: coarse) { summary { min-height: 2.75rem; align-content: center; } }
</style>
